// <copyright file="quote-item-thumbnail-asset.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import type { AttachedBlobAsset } from '../../api/dealer-api-interface-quote';

/**
 * How the picked image travels to the server: as UTF8 text (`data`, used for svg markup) or as
 * raw base64 bytes (`dataBase64`). The two item editors differ here, so the caller states it.
 */
export type ThumbnailAssetEncoding = 'text' | 'base64';

export interface ThumbnailAssetSource {
  /** True only while the uploader is still holding a file the user has just picked. */
  hasNewFile: boolean;
  /** Encoded image produced by the uploader; empty when nothing was picked. */
  content: string;
  assetPath: string;
  encoding: ThumbnailAssetEncoding;
}

/**
 * Builds the `thumbnail` entry of a quote item asset payload, or null when the key must be
 * left out of the payload altogether.
 *
 * INTENT: a save that carries no newly picked image must leave the stored thumbnail alone.
 * `FormInputImageFileUploadView.isValueChanged` is true only while its file input holds a file,
 * and that input is emptied by `ClearFiles()` after every successful save. From the second save
 * of an item onwards an untouched thumbnail is therefore indistinguishable from "no image", so
 * turning that state into `delete: true` silently destroys the stored thumbnail - the server
 * drops the key in MergeAssetLists. That was bug 230276. Omitting the key is the server's
 * documented "no change" signal.
 *
 * There is deliberately no way to express a delete through here: a custom item's thumbnail is
 * mandatory (see the ETO editor's validation) and the uploader offers no clear affordance
 * (`canDelete` is left false), so a delete is never the user's intent.
 */
export function buildThumbnailAsset(source: ThumbnailAssetSource): AttachedBlobAsset | null {
  if (!source.hasNewFile || source.content === '') return null;

  return {
    assetPath: source.assetPath,
    data: source.encoding === 'text' ? source.content : null,
    dataBase64: source.encoding === 'base64' ? source.content : null,
    delete: false,
    // the server recomputes hasContent from the payload it receives
    hasContent: false
  };
}
